import { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { JobsTable } from '@/components/documents/JobsTable';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import {
  ArrowLeft,
  ExternalLink,
  Edit,
  CheckCircle,
  CheckCircle2,
  Trash2,
  Plus,
  Eye,
  FileText,
  Save,
  AlertCircle,
  Send
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface ChunkCollection {
  id: number;
  created_at: string;
  chunks_count: number;
  parse_job?: {
    id: number;
    job_id: string;
    name?: string;
    status: string;
  };
  extract_job?: {
    id: number;
    job_id: string;
    name?: string;
    status: string;
  };
}

interface ParseJob {
  id: number;
  job_id: string;
  name?: string;
  status: string;
}

interface ExtractJob {
  id: number;
  job_id: string;
  name?: string;
  status: string;
}

interface SplitJob {
  id: number;
  job_id?: string;
  status: string;
  start_page?: number;
  end_page?: number;
  header_search_string?: string;
  split_rules?: string;
  start_pages?: any;
  processing_job_id?: string;
}

interface Document {
  id: number;
  name: string;
  description: string;
  status: string;
  vectorized_status: string;
  file_path?: string;
  document_type?: string;
  meeting_date?: string;
  state_abbreviation?: string;
  notes?: string;
  user_name?: string;
  created_at?: string;
  page_count?: number | null;
  'completed?'?: boolean;
  chunk_collections: ChunkCollection[];
  parse_jobs: ParseJob[];
  extract_jobs: ExtractJob[];
  split_jobs: SplitJob[];
}

interface YorickDelivery {
  enabled: boolean;
  deliverable: boolean;
  show: boolean;
  blocked_reason?: string | null;
  last_error?: string | null;
  ingest_state?: 'awaiting_chunks' | 'chunks_never_arrived' | null;
}

interface DocumentShowProps {
  document: Document;
  parse_jobs_pagination: {
    current_page: number;
    total_pages: number;
    per_page: number;
    total_count: number;
  };
  extract_jobs_pagination: {
    current_page: number;
    total_pages: number;
    per_page: number;
    total_count: number;
  };
  flash?: {
    notice?: string | null;
    alert?: string | null;
  };
  yorick_delivery?: YorickDelivery;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  hierarchical: 'Hierarchical',
  meeting_minutes: 'Meeting Minutes',
  demo_document: 'Demo Document',
  uploaded_file: 'File Center Library',
  current_state_law: 'Current State Law',
  potential_state_law: 'Potential State Law'
};

function documentTypeLabel(documentType?: string) {
  if (!documentType) return DOCUMENT_TYPE_LABELS.hierarchical;

  return DOCUMENT_TYPE_LABELS[documentType] || documentType;
}

export default function DocumentShow({ document, parse_jobs_pagination, extract_jobs_pagination, flash, yorick_delivery }: DocumentShowProps) {
  const [notes, setNotes] = useState(document.notes || '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const isStateLawDocument = document.document_type === 'current_state_law' || document.document_type === 'potential_state_law';

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      uploaded: 'bg-orange-100 text-orange-800',
      creating_chunks: 'bg-orange-100 text-orange-800',
      vectorizing: 'bg-blue-100 text-blue-800',
      vectorize_completed: 'bg-green-100 text-green-800',
      vectorize_failed: 'bg-red-100 text-red-800'
    } as const;

    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  const getProgressValue = (status: string) => {
    const progress = {
      pending: 0,
      uploaded: 25,
      processing: 50,
      completed: 100,
      failed: 0,
      creating_chunks: 30,
      vectorizing: 70,
      vectorize_completed: 100,
      vectorize_failed: 0
    } as const;

    return progress[status as keyof typeof progress] || 0;
  };

  const handleAction = (action: string, id?: number) => {
    switch (action) {
      case 'complete':
        router.post(`/documents/${document.id}/complete`);
        break;
      case 'schedule_test_questions':
        router.post(`/documents/${document.id}/schedule_test_questions`);
        break;
      case 'delete_test_questions':
        if (confirm('Are you sure you want to delete ALL test questions for this document?')) {
          router.delete(`/documents/${document.id}/questions`);
        }
        break;
      case 'create_chunk_collections':
        router.post(`/documents/${document.id}/create_chunk_collections`);
        break;
      case 'delete_all_chunk_collections':
        if (confirm('Are you sure you want to delete ALL chunk collections?')) {
          router.delete(`/documents/${document.id}/destroy_all_chunk_collections`);
        }
        break;
      case 'delete_chunk_collection':
        if (id && confirm('Are you sure?')) {
          router.delete(`/documents/${document.id}/chunk_collections/${id}`);
        }
        break;
      case 'delete_parse_job':
        if (id && confirm('Are you sure?')) {
          router.delete(`/documents/${document.id}/parse_jobs/${id}`);
        }
        break;
      case 'delete_extract_job':
        if (id && confirm('Are you sure?')) {
          router.delete(`/documents/${document.id}/extract_jobs/${id}`);
        }
        break;
      case 'delete_split_job':
        if (id && confirm('Are you sure?')) {
          router.delete(`/documents/${document.id}/split_jobs/${id}`);
        }
        break;
      case 'delete_all_parse_jobs':
        if (confirm('Are you sure you want to delete ALL parse jobs for this document?')) {
          router.delete(`/documents/${document.id}/parse_jobs`);
        }
        break;
      case 'delete_all_extract_jobs':
        if (confirm('Are you sure you want to delete ALL extract jobs for this document?')) {
          router.delete(`/documents/${document.id}/extract_jobs`);
        }
        break;
      case 'delete_all_split_jobs':
        if (confirm('Are you sure you want to delete ALL split jobs for this document?')) {
          router.delete(`/documents/${document.id}/split_jobs`);
        }
        break;
      case 'delete_document':
        if (confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
          router.delete(`/documents/${document.id}`);
        }
        break;
      case 'deliver_yorick_metadata':
        router.post(`/documents/${document.id}/deliver_yorick_metadata`);
        break;
    }
  };

  return (
    <>
      <Head title={document.name} />

      <div className="container mx-auto p-6 max-w-6xl">
        <div className="space-y-6">
          {flash?.alert ? (
            <Alert variant="destructive" role="alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription className="whitespace-pre-wrap">{flash.alert}</AlertDescription>
            </Alert>
          ) : null}
          {flash?.notice ? (
            <Alert className="border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-100">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>{flash.notice}</AlertDescription>
            </Alert>
          ) : null}

          {/* Breadcrumbs */}
          <Breadcrumbs opsConsole
            items={[
              { label: document.name, current: true }
            ]}
          />

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <h1 className="text-3xl font-bold text-center lg:text-left">{document.name}</h1>

            <div className="flex flex-wrap justify-center lg:justify-end gap-3">
              <Button asChild variant="outline">
                <Link href="/documents">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Documents
                </Link>
              </Button>

              {document.file_path && (
                <Button asChild variant="outline">
                  <Link href={document.file_path} target="_blank">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Document
                  </Link>
                </Button>
              )}

              <Button asChild variant="outline">
                <Link href={`/documents/${document.id}/edit`}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Document
                </Link>
              </Button>

              <Button
                variant="destructive"
                onClick={() => handleAction('delete_document')}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Document
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 justify-center">
                {!document['completed?'] && (
                  <Button
                    onClick={() => handleAction('complete')}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark Completed
                  </Button>
                )}

                <Button asChild variant="outline">
                  <Link href={`/documents/${document.id}/chunks`}>
                    <FileText className="h-4 w-4 mr-2" />
                    View Chunks
                  </Link>
                </Button>

                <Button asChild variant="outline">
                  <Link href={`/documents/${document.id}/questions`}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Test Questions
                  </Link>
                </Button>

                {document['completed?'] && (
                  <>
                    <Button
                      onClick={() => handleAction('schedule_test_questions')}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Generate All Test Questions
                    </Button>
                    <Button
                      onClick={() => handleAction('delete_test_questions')}
                      variant="destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete All Test Questions
                    </Button>
                  </>
                )}

                {yorick_delivery?.show && (
                  <div className="flex flex-col items-center gap-2 w-full border-t pt-3 mt-1">
                    {yorick_delivery.deliverable ? (
                      <div className="flex flex-col items-center gap-2">
                        <Button
                          onClick={() => handleAction('deliver_yorick_metadata')}
                          disabled={!yorick_delivery.enabled}
                          className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                          title={yorick_delivery.enabled ? 'Send metadata to Yorick and start MinerU processing' : 'Yorick is not configured'}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          Send to Yorick
                        </Button>
                        {yorick_delivery.ingest_state === 'awaiting_chunks' && (
                          <p className="text-sm text-muted-foreground text-center max-w-lg">
                            Yorick accepted this PDF. Chunks have not arrived yet.
                          </p>
                        )}
                        {yorick_delivery.ingest_state === 'chunks_never_arrived' && (
                          <p className="text-sm text-red-600 text-center max-w-lg">
                            Yorick accepted this PDF, but no chunks arrived.
                          </p>
                        )}
                        {yorick_delivery.last_error && (
                          <p className="text-sm text-red-600 text-center max-w-lg">
                            Last Yorick ingest failed: {yorick_delivery.last_error}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center max-w-lg">
                        {yorick_delivery.blocked_reason}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Document Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">{document.description}</p>
              </CardContent>
            </Card>

            {/* Status Information */}
            <Card>
              <CardHeader>
                <CardTitle>Status Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">Document Type:</span>
                    <Badge className="bg-purple-100 text-purple-800">
                      {documentTypeLabel(document.document_type)}
                    </Badge>
                  </div>
                </div>

                {isStateLawDocument && (
                  <div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">State Abbreviation:</span>
                      <span className="text-gray-700">{document.state_abbreviation || 'Not set'}</span>
                    </div>
                  </div>
                )}

                {document.document_type === 'meeting_minutes' && document.meeting_date && (
                  <div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Meeting Date:</span>
                      <span className="text-gray-700">
                        {new Date(document.meeting_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                )}

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">Document Status:</span>
                    <Badge className={getStatusBadge(document.status)}>
                      {document.status.charAt(0).toUpperCase() + document.status.slice(1)}
                    </Badge>
                  </div>
                  <Progress value={getProgressValue(document.status)} className="h-2" />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-semibold">Vectorized Status:</span>
                    <Badge className={getStatusBadge(document.vectorized_status)}>
                      {document.vectorized_status.replace('_', ' ').charAt(0).toUpperCase() +
                       document.vectorized_status.replace('_', ' ').slice(1)}
                    </Badge>
                  </div>
                  <Progress value={getProgressValue(document.vectorized_status)} className="h-2" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Separator />

          {/* Chunk Collections */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Chunk Collections</span>
                <div className="flex gap-2">
                  <Button asChild size="sm">
                    <Link href={`/documents/${document.id}/chunk_collections/new`}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Chunk Collection
                    </Link>
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {document.chunk_collections.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Created At</TableHead>
                        <TableHead>Parse Job ID</TableHead>
                        <TableHead>Extract Job ID</TableHead>
                        <TableHead>Chunks Count</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {document.chunk_collections.map((collection) => (
                        <TableRow key={collection.id}>
                          <TableCell>
                            {new Date(collection.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            {collection.parse_job ? (
                              <div>
                                {collection.parse_job.name && (
                                  <div className="font-semibold">{collection.parse_job.name}</div>
                                )}
                                <div className="text-gray-600">{collection.parse_job.job_id}</div>
                                <Badge className={getStatusBadge(collection.parse_job.status)}>
                                  {collection.parse_job.status}
                                </Badge>
                              </div>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell>
                            {collection.extract_job ? (
                              <div>
                                {collection.extract_job.name && (
                                  <div className="font-semibold">{collection.extract_job.name}</div>
                                )}
                                <div className="text-gray-600">{collection.extract_job.job_id}</div>
                                <Badge className={getStatusBadge(collection.extract_job.status)}>
                                  {collection.extract_job.status}
                                </Badge>
                              </div>
                            ) : (
                              'N/A'
                            )}
                          </TableCell>
                          <TableCell>{collection.chunks_count}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button asChild size="sm" variant="outline">
                                <Link href={`/documents/${document.id}/chunk_collections/${collection.id}/edit`}>
                                  <Edit className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleAction('delete_chunk_collection', collection.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  <div className="flex flex-wrap justify-center gap-3 mt-6">
                    <Button
                      onClick={() => handleAction('create_chunk_collections')}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      Match Parse and Extract Jobs
                    </Button>
                    <Button
                      onClick={() => handleAction('delete_all_chunk_collections')}
                      variant="destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete All Chunk Collections
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No chunk collections have been created yet.</p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
                    <Button asChild>
                      <Link href={`/documents/${document.id}/chunk_collections/new`}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create First Chunk Collection
                      </Link>
                    </Button>
                    <Button
                      onClick={() => handleAction('create_chunk_collections')}
                      className="bg-blue-500 hover:bg-blue-600"
                    >
                      Match Parse and Extract Jobs
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Parse Jobs */}
          <JobsTable
            title="Parse Jobs"
            jobs={document.parse_jobs}
            documentId={document.id}
            jobType="parse"
            onDelete={(id) => handleAction('delete_parse_job', id)}
            onDeleteAll={() => handleAction('delete_all_parse_jobs')}
            pagination={parse_jobs_pagination}
          />

          {/* Extract Jobs */}
          <JobsTable
            title="Extract Jobs"
            jobs={document.extract_jobs}
            documentId={document.id}
            jobType="extract"
            onDelete={(id) => handleAction('delete_extract_job', id)}
            onDeleteAll={() => handleAction('delete_all_extract_jobs')}
            pagination={extract_jobs_pagination}
          />

          {/* Split Jobs */}
          <JobsTable
            title="Split Jobs"
            jobs={document.split_jobs.map(job => ({
              id: job.id,
              job_id: job.job_id || job.processing_job_id || `split-${job.id}`,
              name: job.header_search_string || `Pages ${job.start_page}-${job.end_page}`,
              status: job.status
            }))}
            documentId={document.id}
            jobType="split"
            onDelete={(id) => handleAction('delete_split_job', id)}
            onDeleteAll={() => handleAction('delete_all_split_jobs')}
          />

          <Separator />

          {/* Upload Information */}
          {(document.user_name || document.created_at) && (
            <Card>
              <CardHeader>
                <CardTitle>Upload Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {document.user_name && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-700">Uploaded By:</span>
                    <span className="text-gray-600">{document.user_name}</span>
                  </div>
                )}
                {document.created_at && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-700">Upload Timestamp:</span>
                    <span className="text-gray-600">
                      {new Date(document.created_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Separator />

          {/* Notes Section */}
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Add notes about this document..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[200px] resize-y"
              />
              <div className="flex justify-end">
                <Button
                  onClick={async () => {
                    setIsSavingNotes(true);
                    try {
                      await fetch(`/documents/${document.id}.json`, {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'X-CSRF-Token': window.document.querySelector('meta[name=\"csrf-token\"]')?.getAttribute('content') || ''
                        },
                        body: JSON.stringify({ notes })
                      });
                      router.reload({ only: ['document'] });
                    } catch (error) {
                      console.error('Failed to save notes:', error);
                    } finally {
                      setIsSavingNotes(false);
                    }
                  }}
                  disabled={isSavingNotes}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSavingNotes ? 'Saving...' : 'Save Notes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
